import React from 'react';
import { FormattedMessage } from 'react-intl';
import { gql } from '@apollo/client';
import { useMutation } from 'hooks/apollo';
import translations from 'translations';
import theme from 'shared/constants/theme.json';
import LanguageSelect from 'components/LanguageSelect';

import { Form, Row, Col, Input, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import useGlobalContext from 'hooks/useGlobalContext';
import useFormatMessage from 'hooks/useFormatMessage';
import messages from '../messages';

const SAVE_BASIC_SETTINGS = gql`
  mutation EditReport_SaveBasicSettings($input: UpdateReportInput) {
    updateReport(input: $input) {
      id
      title
      description
      language
      timeOffset
    }
  }
`;

const { TextArea } = Input;

const EditSettings = ({ form, onDone, report }) => {
  const [saveSettings] = useMutation(SAVE_BASIC_SETTINGS);
  const { message } = useGlobalContext();
  const $t = useFormatMessage();

  const onFinish = async (values) => {
    try {
      await saveSettings({
        variables: {
          input: {
            id: report.id,
            title: values.title,
            description: values.description,
            language: values.language,
            timeOffset: parseInt(values.timeOffset, 10),
          },
        },
      });
      message.success($t(messages.settingsSavedMessage));
    } catch (e) {
      console.error(e);
      message.error($t(messages.errorSaving));
    }

    if (onDone) {
      onDone();
    }
  };

  const languages = Object.keys(translations);

  const initialValues = {
    title: report.title,
    language: report.language || languages[0],
    timeOffset: report.timeOffset,
    description: report.description,
  };

  return (
    <Form layout="vertical" onFinish={onFinish} form={form} initialValues={initialValues}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="title"
            label={$t(messages.reportTitleInputLabel)}
            rules={[{ required: true, message: $t(messages.reportTitleInputRequired) }]}
          >
            <Input placeholder={$t(messages.reportTitleInputPlaceholder)} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="language" label={$t(messages.languageFieldLabel)}>
            <LanguageSelect include={languages} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="timeOffset"
            label={$t(messages.reportTimeOffsetFieldLabel)}
            rules={[{ pattern: /^-?\d+$/, message: <FormattedMessage {...messages.reportTimeOffsetFieldError} /> }]}
          >
            <Input suffix={(
              <Tooltip title={<FormattedMessage {...messages.reportTimeOffsetFieldHelp} />}>
                <InfoCircleOutlined style={{ color: theme['text-color-secondary'] }} />
              </Tooltip>
            )}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="description" label={$t(messages.descriptionFieldLabel)}>
            <TextArea rows={4} placeholder={$t(messages.descriptionFieldPlaceholder)} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default EditSettings;
